import React, { useState } from 'react';
import { Edit3, X, ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import { SmallText, Heading, BodyText } from '../../../components/brand/BrandTypography';
import { PrimaryButton, TertiaryButton, IconButton } from '../../../components/brand/BrandButton';
import { BrandCard } from '../../../components/brand/BrandCard';

const AssessmentTab = ({ 
    assessmentConfig, 
    editingAssessmentSection, 
    setEditingAssessmentSection, 
    onSave 
}) => {
    const [expandedSections, setExpandedSections] = useState({});

    if (!assessmentConfig) {
        return (
            <div className="text-center py-12">
                <SmallText className="text-[#7D8E95]">Loading assessment config...</SmallText>
            </div>
        );
    }

    const toggleSection = (section) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    // Define the assessment sections based on actual data structure
    const sections = [
        {
            key: 'welcome',
            title: 'Welcome Screen',
            description: 'The first screen users see when starting the assessment',
            fields: [
                { key: 'title', label: 'Title', type: 'text' },
                { key: 'subtitle', label: 'Subtitle', type: 'text' },
                { key: 'message', label: 'Message', type: 'textarea' },
                { key: 'start_button', label: 'Start Button Text', type: 'text' },
                { key: 'skip_text', label: 'Skip Text', type: 'text' }
            ]
        },
        {
            key: 'primary_need',
            title: 'Primary Need',
            description: 'What brings the user here today',
            fields: [
                { key: 'title', label: 'Title', type: 'text' },
                { key: 'subtitle', label: 'Subtitle', type: 'text' }
            ],
            hasOptions: true
        },
        {
            key: 'body_state',
            title: 'Body State',
            description: 'How the user\'s body feels',
            fields: [
                { key: 'title', label: 'Title', type: 'text' },
                { key: 'subtitle', label: 'Subtitle', type: 'text' }
            ],
            hasOptions: true
        },
        {
            key: 'challenges',
            title: 'Challenges',
            description: 'What the user is experiencing',
            fields: [
                { key: 'title', label: 'Title', type: 'text' },
                { key: 'subtitle', label: 'Subtitle', type: 'text' }
            ],
            hasOptions: true
        },
        {
            key: 'emotions',
            title: 'Emotions',
            description: 'How the user is feeling',
            fields: [
                { key: 'title', label: 'Title', type: 'text' },
                { key: 'subtitle', label: 'Subtitle', type: 'text' },
                { key: 'max_selections', label: 'Max Selections', type: 'number' }
            ]
        },
        {
            key: 'relationship_challenges',
            title: 'Relationship Challenges',
            description: 'Relationship-specific challenges',
            fields: [
                { key: 'title', label: 'Title', type: 'text' },
                { key: 'subtitle', label: 'Subtitle', type: 'text' }
            ],
            hasOptions: true
        },
        {
            key: 'results',
            title: 'Results Screen',
            description: 'The final screen showing recommendations',
            fields: [
                { key: 'title', label: 'Title', type: 'text' },
                { key: 'subtitle', label: 'Subtitle', type: 'text' },
                { key: 'no_results_message', label: 'No Results Message', type: 'text' },
                { key: 'cta_text', label: 'CTA Button Text', type: 'text' }
            ]
        }
    ];

    const handleSaveSection = (sectionKey, newData) => {
        const updatedConfig = { ...assessmentConfig, [sectionKey]: newData };
        onSave(updatedConfig);
        setEditingAssessmentSection(null);
    };

    const renderSectionPreview = (section) => {
        const data = assessmentConfig[section.key];
        if (!data) return <SmallText className="text-[#7D8E95] italic">Not configured</SmallText>;

        return (
            <div className="space-y-2 text-sm">
                {section.fields.slice(0, 3).map(field => (
                    <div key={field.key}>
                        <span className="text-[#7D8E95]">{field.label}:</span>{' '}
                        <span className="text-[#344648]">{data[field.key] || '-'}</span>
                    </div>
                ))}
                {section.hasOptions && data.options && (
                    <div>
                        <span className="text-[#7D8E95]">Options:</span>{' '}
                        <span className="text-[#344648]">{data.options.length} configured</span>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <div className="mb-4 p-4 bg-[#7D8E95]/20 rounded-xl border border-[#7D8E95]/20">
                <SmallText className="text-[#7D8E95]">
                    <strong>Assessment Configuration</strong> - Edit each step of the initial assessment flow including welcome screen, questions, and result descriptions.
                </SmallText>
            </div>
            
            {sections.map((section) => (
                <BrandCard key={section.key} variant="elevated" padding="lg">
                    <div 
                        className="flex items-center justify-between cursor-pointer"
                        onClick={() => toggleSection(section.key)}
                    >
                        <div>
                            <Heading as="h3" size="text-lg">{section.title}</Heading>
                            <SmallText className="text-[#7D8E95]">{section.description}</SmallText>
                        </div>
                        <div className="flex items-center gap-2">
                            <TertiaryButton 
                                size="sm" 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingAssessmentSection({ 
                                        type: section.key, 
                                        data: assessmentConfig[section.key] || {},
                                        section 
                                    });
                                }}
                            >
                                <Edit3 className="w-4 h-4 mr-1" />
                                Edit
                            </TertiaryButton>
                            {expandedSections[section.key] ? (
                                <ChevronUp className="w-5 h-5 text-[#7D8E95]" />
                            ) : (
                                <ChevronDown className="w-5 h-5 text-[#7D8E95]" />
                            )}
                        </div>
                    </div>
                    
                    {expandedSections[section.key] && (
                        <div className="mt-4 pt-4 border-t border-[#FFBB98]/30">
                            {renderSectionPreview(section)}
                            
                            {section.hasOptions && assessmentConfig[section.key]?.options && (
                                <div className="mt-4">
                                    <SmallText className="text-[#7D8E95] font-medium mb-2 block">Options:</SmallText>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                        {assessmentConfig[section.key].options.map((opt, i) => (
                                            <div key={i} className="bg-[#FEF6EF] rounded-lg p-3">
                                                <div className="text-sm font-medium text-[#1D6B6B]">{opt.label}</div>
                                                {opt.description && (
                                                    <div className="text-xs text-[#7D8E95]">{opt.description}</div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </BrandCard>
            ))}
            
            {/* Edit Modal */}
            {editingAssessmentSection && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-medium">
                                Edit {editingAssessmentSection.section?.title || editingAssessmentSection.type}
                            </h3>
                            <IconButton onClick={() => setEditingAssessmentSection(null)}>
                                <X className="w-5 h-5" />
                            </IconButton>
                        </div>
                        
                        <div className="space-y-4">
                            {/* Basic Fields */}
                            {editingAssessmentSection.section?.fields.map(field => (
                                <div key={field.key}>
                                    <label className="text-sm text-[#7D8E95] mb-1 block">{field.label}</label>
                                    {field.type === 'textarea' ? (
                                        <textarea 
                                            value={editingAssessmentSection.data?.[field.key] || ''} 
                                            onChange={(e) => setEditingAssessmentSection({
                                                ...editingAssessmentSection, 
                                                data: {...editingAssessmentSection.data, [field.key]: e.target.value}
                                            })}
                                            className="w-full p-3 border border-[#FFBB98] rounded-xl"
                                            rows={4}
                                        />
                                    ) : field.type === 'number' ? (
                                        <input 
                                            type="number"
                                            value={editingAssessmentSection.data?.[field.key] || ''} 
                                            onChange={(e) => setEditingAssessmentSection({
                                                ...editingAssessmentSection, 
                                                data: {...editingAssessmentSection.data, [field.key]: parseInt(e.target.value) || 0}
                                            })}
                                            className="w-full p-3 border border-[#FFBB98] rounded-xl"
                                        />
                                    ) : (
                                        <input 
                                            value={editingAssessmentSection.data?.[field.key] || ''} 
                                            onChange={(e) => setEditingAssessmentSection({
                                                ...editingAssessmentSection, 
                                                data: {...editingAssessmentSection.data, [field.key]: e.target.value}
                                            })}
                                            className="w-full p-3 border border-[#FFBB98] rounded-xl"
                                        />
                                    )}
                                </div>
                            ))}
                            
                            {/* Options Editor */}
                            {editingAssessmentSection.section?.hasOptions && (
                                <div className="pt-4 border-t border-[#FFBB98]/30">
                                    <div className="flex items-center justify-between mb-3">
                                        <label className="text-sm text-[#7D8E95] font-medium">Options</label>
                                        <TertiaryButton
                                            size="sm"
                                            onClick={() => {
                                                const newOptions = [...(editingAssessmentSection.data?.options || []), {
                                                    id: `option_${Date.now()}`,
                                                    label: 'New Option',
                                                    description: ''
                                                }];
                                                setEditingAssessmentSection({
                                                    ...editingAssessmentSection,
                                                    data: { ...editingAssessmentSection.data, options: newOptions }
                                                });
                                            }}
                                        >
                                            <Plus className="w-4 h-4 mr-1" />
                                            Add Option
                                        </TertiaryButton>
                                    </div>
                                    
                                    <div className="space-y-3">
                                        {editingAssessmentSection.data?.options?.map((opt, i) => (
                                            <div key={i} className="p-3 bg-[#FEF6EF] rounded-xl">
                                                <div className="flex items-start gap-3">
                                                    <div className="flex-1 space-y-2">
                                                        <input 
                                                            value={opt.label || ''} 
                                                            onChange={(e) => {
                                                                const newOptions = [...editingAssessmentSection.data.options];
                                                                newOptions[i] = { ...newOptions[i], label: e.target.value };
                                                                setEditingAssessmentSection({
                                                                    ...editingAssessmentSection,
                                                                    data: { ...editingAssessmentSection.data, options: newOptions }
                                                                });
                                                            }}
                                                            placeholder="Label"
                                                            className="w-full p-2 border border-[#FFBB98] rounded-lg text-sm"
                                                        />
                                                        <input 
                                                            value={opt.description || ''} 
                                                            onChange={(e) => {
                                                                const newOptions = [...editingAssessmentSection.data.options];
                                                                newOptions[i] = { ...newOptions[i], description: e.target.value };
                                                                setEditingAssessmentSection({
                                                                    ...editingAssessmentSection,
                                                                    data: { ...editingAssessmentSection.data, options: newOptions }
                                                                });
                                                            }}
                                                            placeholder="Description (optional)"
                                                            className="w-full p-2 border border-[#FFBB98] rounded-lg text-sm"
                                                        />
                                                    </div>
                                                    <IconButton 
                                                        onClick={() => {
                                                            const newOptions = editingAssessmentSection.data.options.filter((_, idx) => idx !== i);
                                                            setEditingAssessmentSection({
                                                                ...editingAssessmentSection,
                                                                data: { ...editingAssessmentSection.data, options: newOptions }
                                                            });
                                                        }}
                                                        className="text-red-500 hover:bg-red-50"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </IconButton>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        <div className="flex gap-3 mt-6">
                            <TertiaryButton onClick={() => setEditingAssessmentSection(null)} className="flex-1">
                                Cancel
                            </TertiaryButton>
                            <PrimaryButton 
                                onClick={() => handleSaveSection(editingAssessmentSection.type, editingAssessmentSection.data)} 
                                className="flex-1"
                            >
                                Save Changes
                            </PrimaryButton>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AssessmentTab;
